import { User } from 'lucide-react'
import type { ReactNode } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { StatusBadge } from '@/components/atoms'

import { CustomerModel } from '@/types'

interface CustomerProfileCardProps {
  customer: CustomerModel.Customer
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="space-y-1 border-t border-border-subtle px-4 py-3">
    <p className="text-sm font-medium text-ink">{title}</p>
    <div className="text-sm text-text-2">{children}</div>
  </div>
)

export const CustomerProfileCard = ({ customer }: CustomerProfileCardProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center gap-3">
        <div className="size-10 shrink-0 overflow-hidden rounded-full border border-border-subtle bg-muted">
          {customer.avatar ? (
            <img src={customer.avatar} alt={customer.name} className="size-full object-cover" />
          ) : (
            <div className="flex size-full items-center justify-center">
              <User className="size-5 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">{customer.name}</p>
          <p className="truncate text-xs text-muted-foreground">Customer #{customer._id}</p>
        </div>
        <StatusBadge type="user" status={customer.status} />
      </CardContent>

      <Section title="Contact information">
        <p className="truncate">{customer.email || 'No email provided'}</p>
        {customer.phone && <p className="tabular-nums">{customer.phone}</p>}
      </Section>

      {customer.address && (
        <Section title="Default address">
          <div className="break-words">
            {customer.address.street && <p>{customer.address.street}</p>}
            {customer.address.city && customer.address.state && (
              <p>
                {customer.address.city}, {customer.address.state} {customer.address.zip}
              </p>
            )}
            {customer.address.country && <p>{customer.address.country}</p>}
          </div>
        </Section>
      )}

      <Section title="Customer since">
        <p>
          {new Date(customer.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </Section>
    </Card>
  )
}
